import React from 'react';
import { Route, Routes } from 'react-router-dom';

import { Navigate } from '@core/components/navigate';
import { Routes as AppRoutes } from '@core/constants/routes';
import { mergeRoutes } from '@core/lib/merge-routes';

// Layouts
import ActivitiesLayout from '@modules/activities/layouts/activities';
import { PersonalLayout } from '@modules/campaign/pages/personal/layouts/layout';

const PrivateLayout = React.lazy(() => import('@core/layouts/PrivateLayout'));
const PublicLayout = React.lazy(() => import('@core/layouts/PublicLayout'));
const QuestionCreationLayout = React.lazy(
  () => import('@modules/campaign/layouts/QuestionCreationLayout'),
);

// Auth pages
const LoginPage = React.lazy(() => import('../modules/auth/pages/login/Login'));
const CheckInPage = React.lazy(
  () => import('../modules/auth/pages/check-in/check-in'),
);

// Campaign pages
const Campaings = React.lazy(
  () => import('../modules/campaign/pages/campaings/Campaings'),
);
const NewCampaing = React.lazy(
  () => import('../modules/campaign/pages/new-campaign/new-campaign'),
);
const EditCampaign = React.lazy(
  () => import('../modules/campaign/pages/edit-campaign/edit-campaign'),
);
const EditPersonal = React.lazy(
  () =>
    import(
      '../modules/campaign/pages/edit-campaign/components/personal/edit-personal'
    ),
);
const EditPersonalForm = React.lazy(
  () =>
    import(
      '../modules/campaign/pages/edit-campaign/components/personal/edit-personal-form'
    ),
);
const ManageActivities = React.lazy(
  () =>
    import(
      '../modules/campaign/pages/edit-campaign/components/activities/manage-activities'
    ),
);
const EditAddActivity = React.lazy(
  () =>
    import(
      '../modules/campaign/pages/edit-campaign/components/activities/edit-add-activity'
    ),
);
const EditAddSurvey = React.lazy(
  () =>
    import(
      '../modules/campaign/pages/edit-campaign/components/survey/edit-add-survey'
    ),
);
const EditAddQuestion = React.lazy(
  () =>
    import(
      '../modules/campaign/pages/edit-campaign/components/survey/edit-add-question'
    ),
);
const EditShortAnswer = React.lazy(
  () =>
    import(
      '../modules/campaign/pages/edit-campaign/components/questions/edit-short-answer'
    ),
);
const EditParagraph = React.lazy(
  () =>
    import(
      '../modules/campaign/pages/edit-campaign/components/questions/edit-paragraph'
    ),
);
const EditOptions = React.lazy(
  () =>
    import(
      '../modules/campaign/pages/edit-campaign/components/questions/edit-options'
    ),
);
const EditPicture = React.lazy(
  () =>
    import(
      '../modules/campaign/pages/edit-campaign/components/questions/edit-picture'
    ),
);
const EditTours = React.lazy(
  () =>
    import(
      '../modules/campaign/pages/edit-campaign/components/other-activities/edit-tours'
    ),
);
const EditAwards = React.lazy(
  () =>
    import(
      '../modules/campaign/pages/edit-campaign/components/other-activities/edit-awards'
    ),
);
const EditPurchaseInvoice = React.lazy(
  () =>
    import(
      '../modules/campaign/pages/edit-campaign/components/other-activities/edit-purchase-invoice'
    ),
);
const AddActivity = React.lazy(
  () => import('../modules/campaign/pages/add-activity/add-activity'),
);
const NewSurvey = React.lazy(
  () => import('../modules/campaign/pages/new-survey/new-survey'),
);
const AddQuestion = React.lazy(
  () => import('../modules/campaign/pages/add-question/add-question'),
);
const CampaingDetail = React.lazy(
  () => import('../modules/campaign/pages/campaign-details/campaign-details'),
);
const DayDetail = React.lazy(
  () => import('../modules/campaign/pages/day-detail/day-detail'),
);
const SurveyAnswers = React.lazy(
  () => import('../modules/campaign/pages/survey-answers/survey-answers'),
);
const ActivityAnswers = React.lazy(
  () => import('../modules/campaign/pages/activity-answers/activity-answers'),
);
const ShortAnswer = React.lazy(
  () => import('../modules/campaign/pages/short-answer/short-answer'),
);
const Options = React.lazy(
  () => import('../modules/campaign/pages/options/options'),
);
const Picture = React.lazy(
  () => import('../modules/campaign/pages/picture/Picture'),
);
const Paragraph = React.lazy(
  () => import('../modules/campaign/pages/paragraph/Paragraph'),
);
const Awards = React.lazy(
  () => import('../modules/campaign/pages/awards/Awards'),
);
const PurchaseInvoice = React.lazy(
  () => import('../modules/campaign/pages/purchase-invoice/purchase-invoice'),
);
const Tours = React.lazy(() => import('../modules/campaign/pages/tours/Tours'));
const Management = React.lazy(
  () => import('../modules/campaign/pages/management/Management'),
);
const CreatePersonal = React.lazy(
  () =>
    import('../modules/campaign/pages/personal/pages/create/create-personal'),
);
const CreatePerson = React.lazy(
  () => import('../modules/campaign/pages/personal/pages/form/form-personal'),
);
const EmergencyPage = React.lazy(
  () => import('../modules/campaign/pages/emergency/emergency'),
);

// Activities pages
const CampaignAcitivities = React.lazy(
  () => import('../modules/activities/pages/campaign/campaign'),
);
const RespondActivity = React.lazy(
  () => import('../modules/activities/pages/respond/respond'),
);

const RespondCheckpoin = React.lazy(
  () => import('../modules/activities/pages/respond/response-chekpoin'),
);
const ProgressActivity = React.lazy(
  () => import('../modules/activities/pages/progress/progress'),
);
const ProgressChekpoin = React.lazy(
  () => import('../modules/activities/pages/progress/progress-checkpoin'),
);
const Dashboard = React.lazy(
  () => import('../modules/activities/pages/dashboard/dashboard'),
);
const SupervisorActivities = React.lazy(
  () => import('../modules/activities/pages/supervisor/supervisor'),
);
const SupervisorRespond = React.lazy(
  () =>
    import('../modules/activities/pages/supervisor-respond/supervisor-respond'),
);
const SelectPersonal = React.lazy(
  () => import('../modules/activities/pages/select-personal/select-personal'),
);

export const RootRoutes: React.FC = () => {
  return (
    <React.Suspense fallback={<></>}>
      <Routes>
        <Route path={AppRoutes.BASE} Component={PublicLayout}>
          <Route path={AppRoutes.LOGIN} Component={LoginPage} />
        </Route>
        <Route path={AppRoutes.BASE} Component={PrivateLayout}>
          <Route path={AppRoutes.CHECKIN} Component={CheckInPage} />
          <Route path={AppRoutes.CAMPAINGS}>
            <Route index Component={Campaings} />
            <Route path={AppRoutes.ID_CAMPAIGN_PARAM}>
              <Route
                index
                element={
                  <Navigate
                    to={AppRoutes.DETAILS}
                    config={{ omitInitialHash: true }}
                  />
                }
              />
              <Route path={AppRoutes.DETAILS} Component={CampaingDetail} />
              <Route
                path={mergeRoutes([AppRoutes.DAY, AppRoutes.ID_DAY_PARAM], {
                  omitInitialHash: true,
                  omitParams: true,
                })}
                Component={DayDetail}
              />
              <Route
                path={mergeRoutes(
                  [
                    AppRoutes.SURVEY_ANSWERS,
                    AppRoutes.CONFIG_ACTIVITY_ID_PARAM,
                    AppRoutes.USER_ID_PARAM,
                  ],
                  { omitInitialHash: true, omitParams: true },
                )}
                Component={SurveyAnswers}
              />
              <Route
                path={mergeRoutes(
                  [
                    AppRoutes.ACTIVITY_ANSWERS,
                    AppRoutes.ACTIVITY_TYPE_PARAM,
                    AppRoutes.CONFIG_ACTIVITY_ID_PARAM,
                    AppRoutes.USER_ID_PARAM,
                  ],
                  { omitInitialHash: true, omitParams: true },
                )}
                Component={ActivityAnswers}
              />
              <Route path={AppRoutes.MANAGE_PERSONAL} Component={Management} />
            </Route>
            <Route path={AppRoutes.CREATE}>
              <Route index Component={NewCampaing} />
              <Route
                path={mergeRoutes([AppRoutes.ACTIVITY, AppRoutes.ADD], {
                  omitInitialHash: true,
                })}
                Component={AddActivity}
              />
              <Route Component={QuestionCreationLayout} path={AppRoutes.SURVEY}>
                <Route index element={<NewSurvey />} />
                <Route path={AppRoutes.QUESTION} Component={AddQuestion} />
                <Route path={AppRoutes.ADD}>
                  <Route
                    path={AppRoutes.SHORT_ANSWER}
                    Component={ShortAnswer}
                  />
                  <Route path={AppRoutes.PARAGRAPH} Component={Paragraph} />
                  <Route path={AppRoutes.UNIQUE_OPTION} Component={Options} />
                  <Route path={AppRoutes.MULTIPLE_OPTION} Component={Options} />
                  <Route path={AppRoutes.PICTURE} Component={Picture} />
                </Route>
              </Route>

              <Route path={AppRoutes.AWARDS} Component={Awards} />
              <Route path={AppRoutes.INVOICES} Component={PurchaseInvoice} />
              <Route path={AppRoutes.TOURS} Component={Tours} />
              <Route
                path={AppRoutes.MANAGE_PERSONAL}
                Component={PersonalLayout}
              >
                <Route index Component={CreatePersonal} />
                <Route
                  path={AppRoutes.CREATE}
                  element={<CreatePerson isNew />}
                />
                <Route path={AppRoutes.EDIT} Component={CreatePerson} />
              </Route>
            </Route>
            <Route path={AppRoutes.EDIT}>
              <Route path={AppRoutes.ID_CAMPAIGN_PARAM}>
                <Route index Component={EditCampaign} />
                <Route path={AppRoutes.MANAGE_PERSONAL}>
                  <Route index Component={EditPersonal} />
                  <Route
                    path={AppRoutes.CREATE}
                    Component={PersonalLayout}
                  >
                    <Route index element={<CreatePerson isNew />} />
                  </Route>
                  <Route path={AppRoutes.EDIT} Component={EditPersonalForm} />
                </Route>
                <Route
                  path={AppRoutes.MANAGE_ACTIVITIES}
                  Component={ManageActivities}
                />
                <Route
                  path={mergeRoutes([AppRoutes.ACTIVITY, AppRoutes.ADD], {
                    omitInitialHash: true,
                  })}
                  Component={EditAddActivity}
                />
                <Route
                  Component={QuestionCreationLayout}
                  path={AppRoutes.SURVEY}
                >
                  <Route index element={<EditAddSurvey />} />
                  <Route path={AppRoutes.EDIT} element={<EditAddSurvey />} />
                  <Route
                    path={AppRoutes.QUESTION}
                    Component={EditAddQuestion}
                  />
                  <Route path={AppRoutes.ADD}>
                    <Route
                      path={AppRoutes.SHORT_ANSWER}
                      Component={EditShortAnswer}
                    />
                    <Route
                      path={AppRoutes.PARAGRAPH}
                      Component={EditParagraph}
                    />
                    <Route
                      path={AppRoutes.UNIQUE_OPTION}
                      Component={EditOptions}
                    />
                    <Route
                      path={AppRoutes.MULTIPLE_OPTION}
                      Component={EditOptions}
                    />
                    <Route path={AppRoutes.PICTURE} Component={EditPicture} />
                  </Route>
                </Route>
                <Route path={AppRoutes.AWARDS}>
                  <Route index Component={EditAwards} />
                  <Route path={AppRoutes.EDIT} Component={EditAwards} />
                </Route>
                <Route path={AppRoutes.INVOICES}>
                  <Route index Component={EditPurchaseInvoice} />
                  <Route path={AppRoutes.EDIT} Component={EditPurchaseInvoice} />
                </Route>
                <Route path={AppRoutes.TOURS}>
                  <Route index Component={EditTours} />
                  <Route path={AppRoutes.EDIT} Component={EditTours} />
                </Route>
              </Route>
            </Route>
          </Route>
          <Route path={AppRoutes.EMERGENCY} Component={EmergencyPage} />
          <Route path={AppRoutes.DASHBOARD} Component={ActivitiesLayout}>
            <Route index Component={Dashboard} />
            <Route path='check_points'>
              <Route index Component={ProgressChekpoin} />
              <Route
                path={AppRoutes.ACTIVITY_TYPE_ID}
                Component={RespondCheckpoin}
              />
            </Route>
            <Route
              path={AppRoutes.ACTIVITY_TYPE_PARAM}
              Component={RespondActivity}
            />
          </Route>
          <Route path={AppRoutes.ACTIVITIES} Component={ActivitiesLayout}>
            <Route index Component={CampaignAcitivities} />
            <Route path={AppRoutes.ACTIVITY_TYPE_PARAM}>
              <Route index Component={RespondActivity} />
              <Route path={AppRoutes.PROGRESS} Component={ProgressActivity} />
            </Route>
            <Route path={AppRoutes.SUPERVISOR}>
              <Route index Component={SupervisorActivities} />
              <Route
                path={AppRoutes.ACTIVITY_TYPE_PARAM}
                Component={SupervisorRespond}
              />
              <Route
                path={AppRoutes.SELECT_PERSONAL}
                Component={SelectPersonal}
              />
            </Route>
          </Route>
        </Route>
        <Route index element={<Navigate to={AppRoutes.LOGIN} />} />
      </Routes>
    </React.Suspense>
  );
};
